import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import {
  EntityData,
  FilterParameter,
  PaymentFrequency,
  PaymentFrequencyLabel,
  FeeType,
  AccountFilters,
  AccountModificationFlag,
  AccountModificationFlagMap,
  ProductCode,
  QueryParamsService,
  LoanStatus,
  DelinquentStatus
} from 'common';
import {
  AccountQueryResult,
  AccountData,
  AutoPaymentModeMap,
  AutoPaymentMode,
} from '../account.model';
import { AccountService } from '../account.service';
import { AccountFilterService } from '../account-filter.service';
import { AccountListFilterDialogComponent } from '../account-list-filter-dialog/account-list-filter-dialog.component';
import { BrokerData } from '../../broker/broker.model';
import { AdvisorData } from '../../advisor/advisor.model';
import { AppSettings } from '../../../app.settings';
import { UserPermissionService } from "../../user/user-permission/user-permission.service";
import { readOnly, readWrite } from "../../user/user-permission/user-permission.data";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: 'ifb-account-list',
    templateUrl: './account-list.component.html',
    styleUrls: ['./account-list.component.scss'],
    standalone: false
})
export class AccountListComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true })
  paginator: MatPaginator;

  @ViewChild(MatSort)
  sort: MatSort;
  
  queryParams: any;
  filters: AccountFilters;
  activeFilters: FilterParameter[] = [];
  brokerId: number;
  advisorId: number;
  entityId: number;
  canEditAccountFacility: boolean;
  canReadFacility: boolean;

  data: AccountListComponentData;

  readonly ProductCode = ProductCode;
  readonly FeeType = FeeType;
  readonly LoanStatus = LoanStatus;
  readonly DelinquentStatus = DelinquentStatus;

  constructor(
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly accountService: AccountService,
    private readonly settings: AppSettings,
    private readonly _accountFilterService: AccountFilterService,
    private readonly _location: Location,
    private readonly queryParamsService: QueryParamsService,
    private readonly userPermissionService: UserPermissionService) {}

  accountDetailsLink(account: AccountData, newWindow: boolean = false): void {
    this.accountService.prevAccountListUrl = this._location.path().toString();
    const path = `/account/${account.id}/profile`;
    if (newWindow)
      window.open(path);
    else
      this.router.navigate([path]);
  }

  getAccountOfferPaymentFrequenciesLabel(paymentFrequency: PaymentFrequency): string {
    return PaymentFrequencyLabel.get(paymentFrequency);
  }

  getLoanModification(type: AccountModificationFlag): string {
    return AccountModificationFlagMap.get(type);
  }

  getAutoPaymentMode(mode: AutoPaymentMode): string {
    return AutoPaymentModeMap.get(mode);
  }

  private dataInit(data: AccountListComponentData): void {
    this.data = data;
    this.brokerId = this.data.broker ? this.data.broker.id : null;
    this.advisorId = this.data.advisor ? this.data.advisor.id : null;
    this.entityId = this.data.entity ? this.data.entity.id : null;
  }

  ngOnInit(): void {
    this.userPermissionService.granted([readWrite('servicing-account-facilities')])
      .pipe(untilDestroyed(this))
      .subscribe(res => this.canEditAccountFacility = res);

    this.userPermissionService.granted([readOnly('admin-facilities')])
      .pipe(untilDestroyed(this))
      .subscribe(res => this.canReadFacility = res);
    
    this.route.data.pipe(untilDestroyed(this)).subscribe(this.dataInit.bind(this));
    this.route.queryParams.pipe(untilDestroyed(this)).subscribe(it => {
      this.queryParams = this.queryParamsService.init(it);
      this.activeFilters = this._accountFilterService.addFilterParameters(this.queryParams);
      this.filters = new AccountFilters(this.queryParams);
    });
    this.paginator.pageSize = this.queryParams.limit;
    this.paginator.pageIndex = this.queryParams.skip / this.queryParams.limit;
  }

  activeFilterRemoved(activefilters: FilterParameter[]): void {
    if (!activefilters.length) return;
    activefilters.forEach(item => {
      this.filters.remove(item.id);
      this._accountFilterService.filter(this.filters, null, null, this.brokerId, this.advisorId, this.entityId);
    });
  }

  filterDialog(): void {
    AccountListFilterDialogComponent.show(this.dialog, this.queryParams, this.entityId ? false : true)
      .pipe(untilDestroyed(this))
      .subscribe(result => {
        if (result) {
          this.filters = result;
          this.paginator.firstPage();
          this.filter();
        }
      });
  }

  sortData(sort: Sort): void {
    this.filter();
  }

  setPage(event: PageEvent): void {
    this.filter();
  }

  filter(): void{
    this._accountFilterService.filter(this.filters, this.paginator, this.sort, this.brokerId, this.advisorId, this.entityId);
  }

  salesforceLink(salesforceRecordId: string): string {
    return this.settings.salesforce.url + salesforceRecordId;
  }

  download(params: any): void {
    const queryParams = Object.assign({}, params);
    queryParams.brokerId = this.brokerId;
    queryParams.advisorId = this.advisorId;
    queryParams.entityId = this.entityId;
    this.accountService.download(queryParams);
  }

  getDisplayedColumns(): string[] {
    return this.tableColumns
      .filter(cd => this.data.advisor && this.data.advisor.id ? cd.showInAdvisorTab : true)
      .filter(cd => this.data.entity && this.data.entity.id ? cd.showInCompanyTab : true)
      .map(cd => cd.def);
  }
  
  pledgeLoans(): void {
    this.router.navigate(['account/pledge']);
  }

  tableColumns = [
    { def: 'alligatorDataIcon', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'loanNumber', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'entityName', showInAdvisorTab: true, showInCompanyTab: false },
    { def: 'status', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'delinquentStatus', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'facility', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'productType', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'description', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'creditLimit', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'balance', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'msaCreditLimit', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'msaAvailableFunds', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'modification', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'accruedInterest', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'principal', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'paymentAmount', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'paymentDate', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'interestRate', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'drawDownFee', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'drawTerm', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'repaymentTerm', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'paymentFrequency', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'advisorName', showInAdvisorTab: false, showInCompanyTab: true },
    { def: 'createdOn', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'activationDate', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'maturityDate', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'renewalDate', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'paymentDay', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'lastAutoPaymentAmount', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'lastAutoPaymentDate', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'autoPayMode', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'accrueInterest', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'lastReAmortizationDate', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'modificationStartDate', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'modificationEndDate', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'originalPaymentAmount', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'originalRepaymentTerm', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'applicationNumber', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'salesForceID', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'salesForceApplicationID', showInAdvisorTab: true, showInCompanyTab: true },
    { def: 'edit', showInAdvisorTab: true, showInCompanyTab: true }
  ];
}

export interface AccountListComponentData {
  broker?: BrokerData;
  advisor?: AdvisorData;
  entity?: EntityData;
  accountQueryResult: AccountQueryResult;
}
